/**
 * Responsive App Header — Pro Mobile Design
 *
 * Mobile  : Gradient blue bar — hamburger | logo+plant | avatar+bell
 * Desktop : White bar — plant selector (left) | user menu (right)
 */
import { useState } from 'react'
import {
  Layout, Space, Avatar, Dropdown, Typography, Select, Tag,
  Button, Drawer, List, Badge,
} from 'antd'
import {
  UserOutlined, LogoutOutlined, MenuOutlined,
  BankOutlined, DownOutlined, SettingOutlined,
  SwapOutlined,
} from '@ant-design/icons'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '@/features/auth/store'
import { authApi } from '@/features/auth/api'
import { usePlantId } from '@/shared/hooks/usePlantId'
import NotificationBell from './NotificationBell'
import SyncIndicator from './SyncIndicator'

const { Header } = Layout
const { Text } = Typography

interface AppHeaderProps {
  isMobile: boolean
  onMenuClick: () => void
}

export default function AppHeader({ isMobile, onMenuClick }: AppHeaderProps) {
  const navigate = useNavigate()
  const { user, clearAuth, refreshToken } = useAuthStore()
  const { plantId, setPlantId, plants } = usePlantId()
  const [plantDrawerOpen, setPlantDrawerOpen] = useState(false)

  const handleLogout = async () => {
    try { await authApi.logout(refreshToken ?? undefined) } catch { /* ignore */ }
    clearAuth()
    navigate('/login')
  }

  const activePlant = plants.find((p: any) => p.id === plantId) as any

  const userMenuItems = [
    {
      key: 'user-info',
      label: (
        <div style={{ padding: '4px 0', minWidth: 160 }}>
          <div style={{ fontWeight: 700, fontSize: 13 }}>{user?.full_name ?? user?.username}</div>
          <div style={{ color: '#888', fontSize: 11 }}>@{user?.username}</div>
        </div>
      ),
      disabled: true,
    },
    { type: 'divider' as const },
    {
      key: 'settings',
      icon: <SettingOutlined />,
      label: 'Settings',
      onClick: () => navigate('/admin/users'),
    },
    { type: 'divider' as const },
    {
      key: 'logout',
      icon: <LogoutOutlined style={{ color: '#ff4d4f' }} />,
      label: <span style={{ color: '#ff4d4f' }}>Logout</span>,
      onClick: handleLogout,
    },
  ]

  // ── Get user initials ──────────────────────────────────────────────────────
  const initials = (user?.full_name ?? user?.username ?? 'U')
    .split(' ').map((w: string) => w[0]).join('').toUpperCase().slice(0, 2)

  // ─────────────────────────────────────────────────────────────────────────
  // MOBILE HEADER
  // ─────────────────────────────────────────────────────────────────────────
  if (isMobile) {
    return (
      <>
        <Header
          className="erp-mobile-header"
          style={{
            background: 'linear-gradient(135deg, #0958d9 0%, #1677ff 100%)',
            padding: '0 12px',
            height: 56,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            position: 'sticky',
            top: 0,
            zIndex: 100,
            boxShadow: '0 2px 12px rgba(9,88,217,0.3)',
          }}
        >
          {/* Left: hamburger */}
          <Button
            type="text"
            icon={<MenuOutlined style={{ fontSize: 20, color: '#fff' }} />}
            onClick={onMenuClick}
            style={{
              width: 44, height: 44,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              borderRadius: 10,
            }}
          />

          {/* Center: logo + plant pill */}
          <div
            style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1, cursor: 'pointer' }}
            onClick={() => plants.length > 1 && setPlantDrawerOpen(true)}
          >
            <Text style={{ fontSize: 14, fontWeight: 700, color: '#fff', lineHeight: 1.2 }}>
              Spinning Mill ERP
            </Text>
            {activePlant && (
              <div style={{
                display: 'flex', alignItems: 'center', gap: 4,
                background: 'rgba(255,255,255,0.2)',
                borderRadius: 20, padding: '2px 8px', marginTop: 2,
              }}>
                <BankOutlined style={{ fontSize: 10, color: 'rgba(255,255,255,0.9)' }} />
                <span style={{ fontSize: 10, color: '#fff', fontWeight: 500 }}>
                  {activePlant.plant_name ?? activePlant.name}
                </span>
                {plants.length > 1 && (
                  <SwapOutlined style={{ fontSize: 9, color: 'rgba(255,255,255,0.7)' }} />
                )}
              </div>
            )}
          </div>

          {/* Right: sync + bell + avatar */}
          <Space size={4}>
            <SyncIndicator isMobile />
            <NotificationBell />
            <Dropdown menu={{ items: userMenuItems }} placement="bottomRight" trigger={['click']}>
              <Avatar
                style={{
                  background: 'rgba(255,255,255,0.25)',
                  color: '#fff',
                  fontWeight: 700,
                  fontSize: 13,
                  cursor: 'pointer',
                  width: 38, height: 38, lineHeight: '38px',
                  border: '2px solid rgba(255,255,255,0.4)',
                }}
              >
                {initials}
              </Avatar>
            </Dropdown>
          </Space>
        </Header>

        {/* Plant switcher drawer */}
        {plants.length > 1 && (
          <Drawer
            title="Switch Plant"
            placement="bottom"
            height="auto"
            open={plantDrawerOpen}
            onClose={() => setPlantDrawerOpen(false)}
            styles={{ body: { padding: '8px 0 16px' } }}
          >
            <List
              dataSource={plants}
              renderItem={(p: any) => (
                <List.Item
                  style={{
                    padding: '12px 20px',
                    cursor: 'pointer',
                    background: p.id === plantId ? '#e6f4ff' : 'transparent',
                    borderRadius: 8,
                    margin: '2px 8px',
                  }}
                  onClick={() => { setPlantId(p.id); setPlantDrawerOpen(false) }}
                >
                  <Space>
                    <BankOutlined style={{ color: p.id === plantId ? '#1677ff' : '#8c8c8c' }} />
                    <Text strong={p.id === plantId} style={{ color: p.id === plantId ? '#1677ff' : '#262626' }}>
                      {p.plant_name ?? p.name}
                    </Text>
                    {p.id === plantId && <Badge status="processing" text="Active" />}
                  </Space>
                </List.Item>
              )}
            />
          </Drawer>
        )}
      </>
    )
  }

  // ─────────────────────────────────────────────────────────────────────────
  // DESKTOP HEADER
  // ─────────────────────────────────────────────────────────────────────────
  return (
    <Header
      style={{
        background: '#fff',
        padding: '0 20px',
        height: 56,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        borderBottom: '1px solid #f0f0f0',
        position: 'sticky',
        top: 0,
        zIndex: 100,
        boxShadow: '0 1px 6px rgba(0,0,0,0.06)',
      }}
    >
      {/* Left: Plant selector */}
      <Space size={8}>
        <BankOutlined style={{ color: '#1677ff', fontSize: 16 }} />
        {plants.length > 1 ? (
          <Select
            value={plantId || undefined}
            onChange={setPlantId}
            placeholder="Select Plant"
            style={{ minWidth: 180 }}
            size="small"
            variant="outlined"
            options={plants.map((p: any) => ({ value: p.id as string, label: p.plant_name ?? p.name }))}
            suffixIcon={<DownOutlined style={{ fontSize: 10 }} />}
          />
        ) : activePlant ? (
          <Tag color="blue" style={{ margin: 0, fontSize: 13, padding: '3px 10px' }}>
            {activePlant.plant_name ?? activePlant.name}
          </Tag>
        ) : (
          <Text type="secondary" style={{ fontSize: 12 }}>Loading plant…</Text>
        )}
      </Space>

      {/* Right: Sync + Notification bell + User menu */}
      <Space size={8}>
        <SyncIndicator />
        <NotificationBell />
        <Dropdown menu={{ items: userMenuItems }} placement="bottomRight" trigger={['click']}>
          <Space style={{ cursor: 'pointer', padding: '8px 4px' }}>
            <Avatar
              style={{ background: '#1677ff', fontWeight: 700, fontSize: 12 }}
              size={32}
            >
              {initials}
            </Avatar>
            <Text strong style={{ fontSize: 13 }}>
              {user?.full_name ?? user?.username}
            </Text>
            <DownOutlined style={{ fontSize: 10, color: '#888' }} />
          </Space>
        </Dropdown>
      </Space>
    </Header>
  )
}
